import { parsePtacPreferencesRecord } from '@alpinehomeair/common';
import store from 'app/store';
import { isAxiosError } from 'axios';
import {
  AttributeType,
  CustomerAddressType,
  CustomerStructureForDatabaseType,
  CustomerStructureType,
  ExistingSystemType,
  InstanceDataCodeType,
  InstanceDataEntryType,
  LocationAPIResponseType,
  ProjectAttachmentType,
  ProjectDetailsType,
  StructureImageMetadataType,
  StructureItemTypeCode,
  SystemSelectorInstanceType,
  TaxRateType,
  TemperatureDataType,
  TruncatedAttributeType,
  WeatherDataType
} from 'data/types';
import {
  setActiveConfig,
  setConfigSavedAt,
  setConfigurationOptions,
  setConfigurations
} from 'features/configurations';
import { fetchSizingItemDefaults } from 'features/data';
import {
  setAttomData,
  setCustomerAddresses,
  setInstanceUniqueId,
  setLiveChatData,
  setLocationData,
  setProjectAttachments,
  setProjectDetails,
  setProjectSettings,
  setPropertySummary,
  setWeatherData
} from 'features/project';
import { runWithoutStructureHistory } from 'features/structure/structure-history-middleware';
import {
  hydrateRecommendationCatalogCodes,
  hydrateRecommendationSelections,
  resetRecommendationSelections,
  setCondenserLocations,
  setExistingSystems,
  setFootprint,
  setIsNewlyCreatedStructure,
  setNotes,
  setPartialStructure,
  setSatelliteView,
  setStructureActive,
  setStructureAttachments,
  setStructureId,
  setStructureImageAssignments,
  setStructureName,
  setStructureOptions,
  setStructureSubtypeId,
  setStructureTypeId,
  setStructureUniqueId,
  setWantCompositionPicks,
  updateStructureItem
} from 'features/structure/structure-slice';
import {
  setActiveSystemType,
  setSelectedDuctedSystems,
  setSelectedSystems,
  setSystemsToCompare
} from 'features/systems';
import { setZoneDuctModes } from 'features/ui';
import axios from 'utils/api';
import { restoreAttributes } from 'utils/attributes';
import { compositionPicksToHydrate } from 'utils/composition-picks-from-saved-want';
import {
  preserveLocalUserConfigurations,
  resolveActiveConfigurationIndex
} from 'utils/configuration-generation';
import {
  preserveRuntimeValidSystems,
  updateConfigZoneItems
} from 'utils/configurations';
import { isMissingConfigurationDeleteStatus } from 'utils/delete-configurations';
import { typeEmptyConfigurationsForPathway } from 'utils/design-equipment-types';
import { clearAnalyzingFlags } from 'utils/existing-equipment';
import { formatCreateStructureError } from 'utils/format-create-structure-error';
import { getQueryParam, setQueryParam } from 'utils/helpers';
import {
  applyLoadedStructureGenerationFlags,
  applyLoadedStructureLevels
} from 'utils/loaded-structure-levels';
import { mergeRemoteSpaceOpeningPositions } from 'utils/merge-opening-positions';
import { catalogCodeUnion } from 'utils/recommendations-strip-logic';
import { seedDefaultSizingItemInsulationValues } from 'utils/sizing';
import { keepConfigurationsThroughStructureEcho } from 'utils/structure-echo';
import { parseSavedTonnage } from 'utils/tonnage-logic';
import { errorModal, warningModal } from 'utils/ui';
import { parseZoneDuctModes } from 'utils/zone-duct-modes';
import { lookupAddress } from './property';
import { satelliteViewFromMetadata } from './satellite-view';

export function getItem(codeOrId: string | number) {
  return store
    .getState()
    .data.items.find((i) => i.code === codeOrId || i.id === codeOrId);
}

export async function getTaxInfo(zipCode: string) {
  const taxInfoRequest = await axios.get(
    `${process.env.REACT_APP_PARTNERNET_URL}/checkout/tax/rates/${zipCode}`
  );
  const taxInfo = taxInfoRequest.data as TaxRateType;

  return taxInfo;
}

export async function getLocationData(zipCode: string) {
  const locationInfoRequest = await axios.get(
    `${process.env.REACT_APP_PARTNERNET_URL}/location/${zipCode}`
  );
  const locationInfo = locationInfoRequest.data as LocationAPIResponseType;

  const {
    city,
    country,
    latitude,
    longitude,
    state,
    zipCode: zipcode,
    climateZone
  } = locationInfo;

  const taxInfo = await getTaxInfo(zipcode);

  return {
    data: {
      city,
      country,
      latitude,
      longitude,
      state,
      zipcode,
      tax_rate: taxInfo.combined_rate,
      freight_taxable: taxInfo.freight_taxable,
      location: city,
      region: state,
      climateZone
    }
  };
}

export async function getWeatherData(zipCode: string) {
  const temperatureDataRequest = await axios.get('temperature-data', {
    params: {
      zipCode
    }
  });
  const temperatureData = temperatureDataRequest.data as {
    temperatureData: TemperatureDataType[];
    avgMaxSnowDepth: number;
    elevation: number;
  };

  const newWeatherData: WeatherDataType = {
    minTemps: temperatureData.temperatureData.map((t) => ({
      temperature: t.temperatureFahrenheit,
      avgDaysPerYear: t.daysPerYearMinTemperatureIsAtOrBelow
    })),
    maxTemps: temperatureData.temperatureData.map((t) => ({
      temperature: t.temperatureFahrenheit,
      avgDaysPerYear: t.daysPerYearMaxTemperatureIsAtOrAbove
    })),
    avgMaxSnowDepth: temperatureData.avgMaxSnowDepth,
    elevation: temperatureData.elevation
  };

  return newWeatherData;
}

export function loadDefaultProjectSettings() {
  store.dispatch(
    setProjectSettings(getItem('project_settings')?.attributes ?? [])
  );
}

// Heal pre-migration instances that stored a project_settings set predating a
// newly seeded catalog attribute (e.g. installation_timeline, AHA-6796):
// restore the stored settings as before, then append any catalog
// project_settings attribute whose code is missing from the restored set.
// Stored values always win; appended attributes keep their catalog
// default/empty value, so nothing is marked completed. Generic — any future
// catalog addition heals the same way.
export function restoreProjectSettings(
  stored: TruncatedAttributeType[] | undefined | null
): AttributeType[] {
  const catalog = getItem('project_settings')?.attributes ?? [];
  if (!stored || stored.length === 0) return catalog;
  const restored = restoreAttributes(stored);
  const missing = catalog.filter(
    (catAtt) => !restored.some((r) => r.code === catAtt.code)
  );
  return [...restored, ...missing];
}

export async function loadProjectDetails(projectId: number): Promise<void> {
  const { locationData } = store.getState().project;

  try {
    const projectDetailsRequest = await axios.get('project-details', {
      params: {
        projectId
      }
    });
    const newProjectDetails = projectDetailsRequest.data as ProjectDetailsType;

    store.dispatch(setProjectDetails(newProjectDetails));

    if (newProjectDetails.postal) {
      try {
        const newWeatherData = await getWeatherData(newProjectDetails.postal);
        store.dispatch(setWeatherData(newWeatherData));
      } catch (error) {
        console.error(error);
        warningModal('Failed to load weather data');
      }

      if (!locationData) {
        try {
          const newLocationData = await getLocationData(
            newProjectDetails.postal
          );
          store.dispatch(setLocationData(newLocationData.data ?? null));
        } catch (error) {
          console.error(error);
          warningModal('Failed to load location data');
        }
      }
    } else {
      warningModal('No zip code found in project details');
    }
  } catch (error) {
    console.error(error);
    errorModal('Failed to load project details');
  }
}

export async function loadProjectAttachments(projectId: number) {
  try {
    const attachmentsRequest = await axios.get('project-attachments', {
      params: {
        projectId
      }
    });
    const attachments = attachmentsRequest.data as {
      images: ProjectAttachmentType[];
      files: ProjectAttachmentType[];
    };

    store.dispatch(setProjectAttachments(attachments));
  } catch (error) {
    console.error(error);
    errorModal('Failed to load project attachments');
  }
}

export async function loadStructureAttachments(structureId: number) {
  try {
    const attachmentsRequest = await axios.get(
      `${process.env.REACT_APP_PARTNERNET_URL}/api/pnet/structures/${structureId}/photos`
    );
    const attachments = attachmentsRequest.data as ProjectAttachmentType[];

    store.dispatch(setStructureAttachments(attachments));
    return attachments;
  } catch (error) {
    console.error(error);
    errorModal('Failed to load structure attachments');
    return undefined;
  }
}

export async function loadStructureImageMetadata(structureUniqueId: string) {
  try {
    const metadataRequest = await axios.get('structure-image-metadata', {
      params: { structureUniqueId }
    });
    const metadata = metadataRequest.data as StructureImageMetadataType[];

    const assignments: Record<
      number,
      {
        structureItemUniqueId?: string;
        structureItemTypeCode?: StructureItemTypeCode;
        scale?: number | null;
        rotation?: number | null;
        startPositionX?: number | null;
        startPositionY?: number | null;
      } | null
    > = {};
    metadata.forEach((item) => {
      const hasTransformData =
        item.scale != null ||
        item.rotation != null ||
        item.startPositionX != null ||
        item.startPositionY != null;

      if (item.structureItemUniqueId && item.structureItemTypeCode) {
        assignments[item.attachmentId] = {
          structureItemUniqueId: item.structureItemUniqueId,
          structureItemTypeCode: item.structureItemTypeCode,
          scale: item.scale,
          rotation: item.rotation,
          startPositionX: item.startPositionX,
          startPositionY: item.startPositionY
        };
      } else if (hasTransformData) {
        assignments[item.attachmentId] = {
          scale: item.scale,
          rotation: item.rotation,
          startPositionX: item.startPositionX,
          startPositionY: item.startPositionY
        };
      } else {
        assignments[item.attachmentId] = null;
      }
    });

    store.dispatch(setStructureImageAssignments(assignments));
    store.dispatch(setSatelliteView(satelliteViewFromMetadata(metadata)));
  } catch (error) {
    console.error('Failed to load structure image metadata:', error);
  }
}

export async function refetchStructureAttachments(
  structureUniqueId?: string
): Promise<void> {
  const currentStructureId = store.getState().structure.structureId;

  if (structureUniqueId !== store.getState().structure.structureUniqueId) {
    return;
  }

  if (!currentStructureId) {
    return;
  }

  try {
    await loadStructureAttachments(currentStructureId as number);
  } catch (error) {
    console.error('Failed to refetch structure attachments:', error);
  }
}

export async function loadLiveChatData(projectId: number) {
  try {
    const liveChatDataRequest = await axios.get('live-chat-data', {
      params: {
        projectId
      }
    });
    const liveChatData = liveChatDataRequest.data;

    store.dispatch(setLiveChatData(liveChatData));
  } catch (error: any) {
    console.error(error);

    if (error.response?.status !== 404) {
      errorModal('Failed to load live chat data');
    }
  }
}

export async function loadCustomerAddresses(projectId: number) {
  try {
    const addressesRequest = await axios.get('project-customer-addresses', {
      params: {
        projectId
      }
    });
    const addresses = addressesRequest.data as string[];

    store.dispatch(setCustomerAddresses(addresses));
  } catch (error: any) {
    console.error('Failed to load customer addresses:', error);
  }
}

export async function loadProject(projectId: number) {
  await Promise.all([
    loadProjectDetails(projectId),
    loadProjectAttachments(projectId),
    loadLiveChatData(projectId),
    loadCustomerAddresses(projectId)
  ]);
}

export async function getStructureAddress(
  structureUniqueId: string
): Promise<CustomerAddressType | null> {
  try {
    const addressRequest = await axios.get('address', {
      params: { structureUniqueId }
    });
    return (addressRequest.data as CustomerAddressType) ?? null;
  } catch (error) {
    console.error('Failed to get address:', error);
    return null;
  }
}

export async function loadPropertyData(
  address: CustomerAddressType
): Promise<boolean> {
  try {
    await lookupAddress(
      `${address.address1}, ${address.city}, ${address.state} ${address.zip}`
    );
    return true;
  } catch (error) {
    console.error(error);
    errorModal('Failed to load property data');
    return false;
  }
}

export async function fetchInstanceData(
  instanceUniqueId: string,
  codes: InstanceDataCodeType[]
): Promise<Partial<Record<InstanceDataCodeType, string | null>>> {
  try {
    const res = await axios.get('instance/data', {
      params: { instanceUniqueId, codes: codes.join(',') }
    });
    const entries = (res.data?.data ?? []) as InstanceDataEntryType[];
    return Object.fromEntries(
      entries.map((entry) => [entry.code, entry.value])
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load instance data', error);
    return {};
  }
}

export async function loadInstance(instance: SystemSelectorInstanceType) {
  console.groupCollapsed('Loading instance', instance.uniqueId.slice(-5));
  console.log(instance);
  console.groupEnd();

  const instanceData = await fetchInstanceData(instance.uniqueId, [
    'property-summary',
    'zone-duct-modes'
  ]);

  store.dispatch(setPropertySummary(instanceData['property-summary'] ?? null));
  store.dispatch(
    setZoneDuctModes(
      parseZoneDuctModes(instanceData['zone-duct-modes'] ?? null)
    )
  );
  store.dispatch(setInstanceUniqueId(instance.uniqueId));
  store.dispatch(
    setProjectSettings(restoreProjectSettings(instance.projectSettings))
  );
  store.dispatch(setAttomData(instance.propertyData));
  store.dispatch(setLocationData(instance.locationData));
  store.dispatch(
    setConfigurationOptions(
      updateConfigZoneItems(instance.configurationOptions ?? [])
    )
  );
  store.dispatch(
    setConfigurations(
      typeEmptyConfigurationsForPathway(
        updateConfigZoneItems(instance.configurations ?? [])
      )
    )
  );
  store.dispatch(setConfigSavedAt(Date.now()));
  store.dispatch(setSelectedSystems(instance.selectedSystems?.ductless ?? []));
  store.dispatch(
    setSelectedDuctedSystems(instance.selectedSystems?.ducted ?? [])
  );
  store.dispatch(
    setActiveSystemType(
      (instance.selectedSystems?.ducted ?? []).length > 0
        ? 'ducted'
        : 'ductless'
    )
  );
  store.dispatch(setSystemsToCompare(instance.systemsToCompare ?? []));
}

// Ducted-gate hydration (AHA-6681 batch 1): mirror each system's persisted
// strip selections into Redux so the Existing Equipment gate is reload-safe
// before the page ever mounts. The want picker's composition (fuel and
// equipment) is rebuilt from the saved want id and the context catalog —
// that state otherwise exists only while Equipment Preferences is mounted,
// so a reload on a later page left the header step unchecked. Fire-and-
// forget from loadStructure — the page-progress watcher treats the
// unhydrated window as 0%.
async function hydrateStructureRecommendationSelections(
  existingSystems: ExistingSystemType[]
) {
  const results = await Promise.all(
    existingSystems.map(async (system) => {
      try {
        const response = await axios.get(
          `/existing-equipment/recommendation-context/${system.uniqueId}`
        );
        // Full-universe catalog codes (AHA-6796 fuel vocabulary) ride the
        // same GET — extracted here so the fuel rows never pay an extra
        // fetch. A failed GET stores nothing (bound fallback applies).
        const catalogCodes = catalogCodeUnion(response.data?.systemTypes);
        const saved = response.data?.savedSelections;
        if (!saved) {
          return {
            systemUniqueId: system.uniqueId,
            entry: null,
            catalogCodes,
            systemTypes: response.data?.systemTypes
          };
        }
        // Full-record mirror (AHA-6681 tonnage): explicit picks so the
        // server-managed lastResults never enters the mirror; tonnage is
        // re-parsed defensively (the API's tolerant read already drops
        // malformed rows, but an older API passes it raw).
        const parsedTonnage = parseSavedTonnage(saved.tonnage);
        const parsedPtac = parsePtacPreferencesRecord(saved.ptac);
        return {
          systemUniqueId: system.uniqueId,
          entry: {
            wantIds: saved.wantIds ?? [],
            haveId: saved.haveId,
            refrigerant: saved.refrigerant,
            age: saved.age,
            useRecommendations: saved.useRecommendations,
            fingerprint: saved.fingerprint,
            virtualCodes: saved.virtualCodes,
            virtualFuel: saved.virtualFuel,
            ...(parsedTonnage ? { tonnage: parsedTonnage } : {}),
            ...(parsedPtac ? { ptac: parsedPtac } : {})
          },
          catalogCodes,
          systemTypes: response.data?.systemTypes
        };
      } catch (error) {
        console.error(
          'Failed to hydrate recommendation selections for system',
          system.uniqueId,
          error
        );
        return {
          systemUniqueId: system.uniqueId,
          entry: null,
          catalogCodes: null,
          systemTypes: null
        };
      }
    })
  );
  // Catalogs BEFORE selections (AHA-6796): the recommendationSelectionsLoaded
  // gate that arms the ducted context reset flips with the selections
  // dispatch, so the reset always sees the catalogs on load paths.
  store.dispatch(
    hydrateRecommendationCatalogCodes(
      Object.fromEntries(
        results
          .filter((result) => result.catalogCodes !== null)
          .map((result) => [result.systemUniqueId, result.catalogCodes!])
      )
    )
  );
  results.forEach((result) => {
    const picks = compositionPicksToHydrate(
      store.getState().structure.wantCompositionPicksBySystem[
        result.systemUniqueId
      ],
      result.systemTypes,
      result.entry?.wantIds
    );
    if (picks) {
      store.dispatch(
        setWantCompositionPicks({
          systemUniqueId: result.systemUniqueId,
          picks
        })
      );
    }
  });
  store.dispatch(
    hydrateRecommendationSelections(
      Object.fromEntries(
        results.map((result) => [result.systemUniqueId, result.entry])
      )
    )
  );
}

export async function loadStructure(structure: CustomerStructureType) {
  console.groupCollapsed('Loading structure', structure.uniqueId.slice(-5));
  console.log(structure);
  console.groupEnd();

  store.dispatch(setSatelliteView(null));
  await Promise.all([
    loadStructureAttachments(structure.id),
    loadStructureImageMetadata(structure.uniqueId)
  ]);
  store.dispatch(setStructureUniqueId(structure.uniqueId));
  store.dispatch(setStructureId(structure.id));
  store.dispatch(setStructureName(structure.name));
  store.dispatch(setStructureActive(structure.active ?? true));
  store.dispatch(setStructureTypeId(structure.typeId));
  store.dispatch(setStructureSubtypeId(structure.subtypeId));
  store.dispatch(setPartialStructure(structure.isPartial));
  const loadedExistingSystems = clearAnalyzingFlags(
    structure.existingEquipment ?? []
  );
  store.dispatch(setExistingSystems(loadedExistingSystems));
  // Reset first so a structure switch can never unlock the ducted gate on
  // the previous structure's selections; hydration is not awaited.
  store.dispatch(resetRecommendationSelections());
  hydrateStructureRecommendationSelections(loadedExistingSystems);
  applyLoadedStructureLevels(structure);

  store.dispatch(setNotes(structure.notes ?? ''));
  if (structure.footprint) {
    store.dispatch(setFootprint(structure.footprint));
  }
  store.dispatch(setCondenserLocations(structure.condenserLocations ?? null));

  applyLoadedStructureGenerationFlags(structure);
  store.dispatch(fetchSizingItemDefaults(structure.uniqueId));

  setQueryParam('structureId', structure.uniqueId);
}

export async function refetchStructure(
  structureUniqueId?: string
): Promise<void> {
  const currentStructureId =
    structureUniqueId || store.getState().structure.structureUniqueId;

  if (!currentStructureId) {
    console.warn('No structure ID provided for refetch');
    return;
  }

  try {
    const structureRequest = await axios.get('structure', {
      params: { uniqueId: currentStructureId }
    });
    const structure = structureRequest.data as CustomerStructureType;
    await loadStructure(structure);
  } catch (error) {
    console.error('Failed to refetch structure:', error);
  }
}

export async function refetchStructureLevels(
  structureUniqueId?: string
): Promise<void> {
  const currentStructureId =
    structureUniqueId || store.getState().structure.structureUniqueId;

  if (!currentStructureId) {
    console.warn('No structure ID provided for levels refetch');
    return;
  }

  try {
    const structureRequest = await axios.get('structure', {
      params: { uniqueId: currentStructureId }
    });
    const structure = structureRequest.data as CustomerStructureType;
    applyLoadedStructureLevels(structure);
    applyLoadedStructureGenerationFlags(structure);
  } catch (error) {
    console.error('Failed to refetch structure levels:', error);
  }
}

export async function refetchStructureSpace(
  spaceUniqueId: string,
  structureUniqueId?: string
): Promise<void> {
  const currentStructureId =
    structureUniqueId || store.getState().structure.structureUniqueId;

  if (!currentStructureId) {
    console.warn('No structure ID provided for space refetch');
    return;
  }

  try {
    const structureRequest = await axios.get('structure', {
      params: { uniqueId: currentStructureId }
    });
    const structure = structureRequest.data as CustomerStructureType;

    const updatedSpace = structure.levels
      ?.flatMap((level) => level.sections)
      .flatMap((section) => section.spaces)
      .find((space) => space.uniqueId === spaceUniqueId);

    if (updatedSpace) {
      const restoredSpaceAttributes = restoreAttributes(
        updatedSpace.attributes
      );

      const {
        position,
        width,
        length,
        vertices,
        ...spaceWithoutPositionalData
      } = updatedSpace;

      const spaceWithRestoredAttributes = {
        ...spaceWithoutPositionalData,
        attributes: restoredSpaceAttributes,
        sizingAttributes:
          mergeRemoteSpaceOpeningPositions(
            updatedSpace.uniqueId,
            updatedSpace.sizingAttributes,
            store.getState().structure.structure
          ) ?? updatedSpace.sizingAttributes
      };

      runWithoutStructureHistory(() => {
        store.dispatch(updateStructureItem(spaceWithRestoredAttributes));
      });
    } else {
      console.warn('Space not found:', spaceUniqueId);
    }
  } catch (error) {
    console.error('Failed to refetch space:', error);
  }
}

export async function refetchStructureSpaces(
  spaceUniqueIds: string[],
  structureUniqueId?: string
): Promise<void> {
  if (spaceUniqueIds.length === 0) {
    return;
  }

  const currentStructureId =
    structureUniqueId || store.getState().structure.structureUniqueId;

  if (!currentStructureId) {
    console.warn('No structure ID provided for spaces refetch');
    return;
  }

  try {
    const structureRequest = await axios.get('structure', {
      params: { uniqueId: currentStructureId }
    });
    const structure = structureRequest.data as CustomerStructureType;

    const allSpaces = structure.levels
      ?.flatMap((level) => level.sections)
      .flatMap((section) => section.spaces);

    const spaceUniqueIdSet = new Set(spaceUniqueIds);
    const matchedSpaces = allSpaces?.filter((space) =>
      spaceUniqueIdSet.has(space.uniqueId)
    );

    if (matchedSpaces && matchedSpaces.length > 0) {
      runWithoutStructureHistory(() => {
        for (const updatedSpace of matchedSpaces) {
          const restoredSpaceAttributes = restoreAttributes(
            updatedSpace.attributes
          );

          store.dispatch(
            updateStructureItem({
              uniqueId: updatedSpace.uniqueId,
              attributes: restoredSpaceAttributes,
              ...(updatedSpace.sizingAttributes
                ? {
                    sizingAttributes:
                      mergeRemoteSpaceOpeningPositions(
                        updatedSpace.uniqueId,
                        updatedSpace.sizingAttributes,
                        store.getState().structure.structure
                      ) ?? updatedSpace.sizingAttributes
                  }
                : {})
            })
          );
        }
        // Same batch as the attribute echo above: the server-derived
        // attributes must not read as a user structure edit that wipes the
        // configurations (ResetDataWatcher).
        keepConfigurationsThroughStructureEcho();
      });
    }
  } catch (error) {
    console.error('Failed to refetch spaces:', error);
  }
}

export async function refetchStructureMetadata(
  structureUniqueId?: string
): Promise<void> {
  const currentStructureId =
    structureUniqueId || store.getState().structure.structureUniqueId;

  if (!currentStructureId) {
    console.warn('No structure ID provided for metadata refetch');
    return;
  }

  try {
    const structureRequest = await axios.get('structure', {
      params: { uniqueId: currentStructureId }
    });
    const structure = structureRequest.data as CustomerStructureType;

    store.dispatch(setStructureName(structure.name));
    store.dispatch(setStructureTypeId(structure.typeId));
    store.dispatch(setStructureSubtypeId(structure.subtypeId));
    store.dispatch(setPartialStructure(structure.isPartial));
    store.dispatch(setNotes(structure.notes ?? ''));
    console.log('Refetched and updated structure metadata in state');
  } catch (error) {
    console.error('Failed to refetch structure metadata:', error);
  }
}

export async function refetchInstance(): Promise<void> {
  const { structureUniqueId } = store.getState().structure;
  const { projectDetails } = store.getState().project;

  if (!projectDetails?.projectId) {
    console.warn('No project ID available for instance refetch');
    return;
  }

  try {
    const instanceRequest = await axios.get('instance', {
      params: {
        projectId: projectDetails.projectId,
        ...(structureUniqueId && { structureUniqueId })
      }
    });
    const instance = instanceRequest.data as SystemSelectorInstanceType;
    await loadInstance(instance);
  } catch (error) {
    console.error('Failed to refetch instance:', error);
  }
}

export async function refetchInstanceConfigurations(): Promise<void> {
  const { structureUniqueId } = store.getState().structure;
  const { projectDetails } = store.getState().project;

  if (!projectDetails?.projectId) {
    console.warn('No project ID available for configurations refetch');
    return;
  }

  try {
    const instanceRequest = await axios.get('instance', {
      params: {
        projectId: projectDetails.projectId,
        ...(structureUniqueId && { structureUniqueId })
      }
    });
    const instance = instanceRequest.data as SystemSelectorInstanceType;

    store.dispatch(setInstanceUniqueId(instance.uniqueId));

    const {
      configurations: existingConfigurations,
      activeConfig,
      protectedConfigurationIdsDuringGeneration,
      unsavedLocallyCreatedConfigurationIds
    } = store.getState().configurations;
    const hydratedConfigurations = preserveRuntimeValidSystems(
      typeEmptyConfigurationsForPathway(
        updateConfigZoneItems(instance.configurations ?? [])
      ),
      existingConfigurations
    );
    const protectedConfigurationIds = store.getState().ai.configGenerating
      ? protectedConfigurationIdsDuringGeneration
      : unsavedLocallyCreatedConfigurationIds;
    const nextConfigurations = preserveLocalUserConfigurations(
      hydratedConfigurations,
      existingConfigurations,
      protectedConfigurationIds
    );
    store.dispatch(setConfigurations(nextConfigurations));
    const nextActiveConfig = resolveActiveConfigurationIndex(
      nextConfigurations,
      existingConfigurations[activeConfig]
    );
    if (nextActiveConfig !== activeConfig) {
      store.dispatch(setActiveConfig(nextActiveConfig));
    }
    store.dispatch(
      setConfigurationOptions(
        updateConfigZoneItems(instance.configurationOptions ?? [])
      )
    );
    store.dispatch(setConfigSavedAt(Date.now()));
    console.log('Refetched and updated instance configurations in state');
  } catch (error) {
    console.error('Failed to refetch instance configurations:', error);
  }
}

export type DeleteConfigurationsResult = {
  deletedUniqueIds: string[];
  failedUniqueIds: string[];
};

export async function deleteConfigurations(
  uniqueIds: string[]
): Promise<DeleteConfigurationsResult> {
  return uniqueIds.reduce<Promise<DeleteConfigurationsResult>>(
    async (previous, uniqueId) => {
      const result = await previous;
      try {
        await axios.delete('configuration', {
          params: { uniqueId: String(uniqueId) }
        });
        return {
          deletedUniqueIds: [...result.deletedUniqueIds, uniqueId],
          failedUniqueIds: result.failedUniqueIds
        };
      } catch (error) {
        if (
          isAxiosError(error) &&
          isMissingConfigurationDeleteStatus(error.response?.status)
        ) {
          return {
            deletedUniqueIds: [...result.deletedUniqueIds, uniqueId],
            failedUniqueIds: result.failedUniqueIds
          };
        }
        console.error('Failed to delete configuration:', uniqueId, error);
        return {
          deletedUniqueIds: result.deletedUniqueIds,
          failedUniqueIds: [...result.failedUniqueIds, uniqueId]
        };
      }
    },
    Promise.resolve({ deletedUniqueIds: [], failedUniqueIds: [] })
  );
}

export async function refetchInstanceSystems(): Promise<void> {
  const { structureUniqueId } = store.getState().structure;
  const { projectDetails } = store.getState().project;

  if (!projectDetails?.projectId) {
    console.warn('No project ID available for systems refetch');
    return;
  }

  try {
    const instanceRequest = await axios.get('instance', {
      params: {
        projectId: projectDetails.projectId,
        ...(structureUniqueId && { structureUniqueId })
      }
    });
    const instance = instanceRequest.data as SystemSelectorInstanceType;

    store.dispatch(
      setSelectedSystems(instance.selectedSystems?.ductless ?? [])
    );
    store.dispatch(
      setSelectedDuctedSystems(instance.selectedSystems?.ducted ?? [])
    );
    store.dispatch(
      setActiveSystemType(
        (instance.selectedSystems?.ducted ?? []).length > 0
          ? 'ducted'
          : 'ductless'
      )
    );
    console.log('Refetched and updated instance systems in state');
  } catch (error) {
    console.error('Failed to refetch instance systems:', error);
  }
}

export async function refetchInstanceSettings(): Promise<void> {
  const { structureUniqueId } = store.getState().structure;
  const { projectDetails } = store.getState().project;

  if (!projectDetails?.projectId) {
    console.warn('No project ID available for settings refetch');
    return;
  }

  try {
    const instanceRequest = await axios.get('instance', {
      params: {
        projectId: projectDetails.projectId,
        ...(structureUniqueId && { structureUniqueId })
      }
    });
    const instance = instanceRequest.data as SystemSelectorInstanceType;

    store.dispatch(
      setProjectSettings(restoreProjectSettings(instance.projectSettings))
    );
    console.log('Refetched and updated instance settings in state');
  } catch (error) {
    console.error('Failed to refetch instance settings:', error);
  }
}

export async function refetchProject(projectId?: number): Promise<void> {
  const currentProjectId =
    projectId || store.getState().project.projectDetails?.projectId;

  if (!currentProjectId) {
    console.warn('No project ID provided for refetch');
    return;
  }

  try {
    await loadProject(currentProjectId);
  } catch (error) {
    console.error('Failed to refetch project:', error);
  }
}

export async function refetchProjectDetails(projectId?: number): Promise<void> {
  const currentProjectId =
    projectId || store.getState().project.projectDetails?.projectId;

  if (!currentProjectId) {
    console.warn('No project ID provided for project details refetch');
    return;
  }

  try {
    await loadProjectDetails(currentProjectId);
    console.log('Refetched and updated project details in state');
  } catch (error) {
    console.error('Failed to refetch project details:', error);
  }
}

export async function refetchProjectAttachments(
  projectId?: number
): Promise<void> {
  const currentProjectId =
    projectId || store.getState().project.projectDetails?.projectId;

  if (!currentProjectId) {
    console.warn('No project ID provided for project attachments refetch');
    return;
  }

  try {
    await loadProjectAttachments(currentProjectId);
    console.log('Refetched and updated project attachments in state');
  } catch (error) {
    console.error('Failed to refetch project attachments:', error);
  }
}

async function loadInstanceForStructure(
  projectId: number,
  structureUniqueId: string,
  instanceIdParam: string | null
): Promise<boolean> {
  try {
    const loadInstanceRequest = await axios.get('instance', {
      params: instanceIdParam
        ? { uniqueId: instanceIdParam }
        : { projectId, structureUniqueId }
    });
    const instance = loadInstanceRequest.data as SystemSelectorInstanceType;

    await loadInstance(instance);

    if (!instance.propertyData) {
      const address = await getStructureAddress(structureUniqueId);
      if (address) {
        await loadPropertyData(address);
      }
    }
    return true;
  } catch {
    return false;
  }
}

async function handleMissingInstance(
  structureIdParam: string
): Promise<boolean> {
  warningModal('No instance found - a new one will be created');

  try {
    const addressRequest = await axios.get('address', {
      params: { structureUniqueId: structureIdParam }
    });
    const address = addressRequest.data as CustomerAddressType;

    if (address) {
      const success = await loadPropertyData(address);
      if (!success) {
        return false;
      }
    }

    loadDefaultProjectSettings();
    return true;
  } catch (error) {
    console.error(error);
    errorModal('Failed to load address');
    return false;
  }
}

async function loadExplicitStructure(
  projectId: number,
  structureIdParam: string,
  instanceIdParam: string | null
): Promise<boolean> {
  try {
    const loadStructureRequest = await axios.get('structure', {
      params: { uniqueId: structureIdParam }
    });
    const structure = loadStructureRequest.data as CustomerStructureType;

    if (!structure) {
      console.error('Structure not found:', structureIdParam);
      errorModal('Structure not found');
      return false;
    }

    await loadStructure(structure);

    const instanceLoaded = await loadInstanceForStructure(
      projectId,
      structureIdParam,
      instanceIdParam
    );

    if (!instanceLoaded) {
      return await handleMissingInstance(structureIdParam);
    }

    return true;
  } catch (error) {
    console.error(error);
    errorModal('Failed to load structure');
    return false;
  }
}

async function loadFirstAvailableStructure(
  projectId: number,
  instanceIdParam: string | null,
  structuresWithoutLevels: CustomerStructureForDatabaseType[]
): Promise<boolean> {
  try {
    const loadStructureRequest = await axios.get('structure', {
      params: { uniqueId: structuresWithoutLevels[0].uniqueId }
    });
    const structure = loadStructureRequest.data as CustomerStructureType;

    await loadStructure(structure);

    const instanceLoaded = await loadInstanceForStructure(
      projectId,
      structuresWithoutLevels[0].uniqueId,
      instanceIdParam
    );

    if (!instanceLoaded) {
      warningModal('No instance found - a new one will be created');
      loadDefaultProjectSettings();
    }

    return true;
  } catch (error) {
    console.error(error);
    errorModal('Failed to load structure');
    return false;
  }
}

async function createNewStructure(projectId: number): Promise<boolean> {
  try {
    const newStructureUniqueId = crypto.randomUUID();
    const createStructureRequest = await axios.patch('structure', {
      projectId,
      structure: {
        uniqueId: newStructureUniqueId,
        name: 'New Structure',
        typeId: null,
        subtypeId: null,
        isPartial: false,
        existingEquipment: [],
        notes: ''
      }
    });
    const { structureUniqueId } = createStructureRequest.data;
    const structureRequest = await axios.get('structure', {
      params: { uniqueId: structureUniqueId }
    });
    const structure = structureRequest.data as CustomerStructureType;

    await loadStructure(structure);
    // Mark the structure so the insulation seed PERSISTS fresh year-built
    // defaults instead of hydrating over the global ones (see utils/sizing
    // seedDefaultSizingItemInsulationValues). When ATTOM data already
    // arrived, the ATTOM-driven trigger (utils/property lookupAddress) has
    // come and gone, so re-run the seed now — otherwise the flag stays
    // armed for that trigger. When that chain is still in flight
    // (isLocationDataPending), skip the immediate seed and leave the flag
    // armed so the in-flight callback (or its catch fallback) persists
    // with the best available zone — exactly one persist-seed wins. The
    // seed never rejects by contract, so this await cannot trip the catch
    // below.
    store.dispatch(setIsNewlyCreatedStructure(true));
    const { attomData, locationData, isLocationDataPending } =
      store.getState().project;
    if (attomData && !isLocationDataPending) {
      await seedDefaultSizingItemInsulationValues(
        attomData,
        locationData?.climateZone ?? '4'
      );
    }
    return true;
  } catch (error) {
    console.error(error);
    errorModal(formatCreateStructureError(error));
    return false;
  }
}

async function loadProjectWithStructure(
  projectId: number,
  structureIdParam: string | null,
  instanceIdParam: string | null,
  projectStructures: CustomerStructureForDatabaseType[]
): Promise<boolean> {
  if (structureIdParam && structureIdParam.toLowerCase() !== 'new') {
    return loadExplicitStructure(projectId, structureIdParam, instanceIdParam);
  }

  if (!structureIdParam && projectStructures.length > 0) {
    return loadFirstAvailableStructure(
      projectId,
      instanceIdParam,
      projectStructures
    );
  }

  if (
    (!structureIdParam && projectStructures.length < 1) ||
    structureIdParam?.toLowerCase() === 'new'
  ) {
    const created = await createNewStructure(projectId);
    if (!created) {
      return false;
    }
  }

  loadDefaultProjectSettings();
  return true;
}

export async function initializeApp(): Promise<{ success: boolean }> {
  const projectIdParam = getQueryParam('projectId');
  const structureIdParam = getQueryParam('structureId');
  const instanceIdParam = getQueryParam('instanceId');
  const projectId = projectIdParam ? Number(projectIdParam) : null;

  if (!projectId) {
    loadDefaultProjectSettings();
    warningModal('No project ID provided. Auto-saving will be disabled.');
    return { success: false };
  }

  await loadProject(projectId);

  const structuresRequest = await axios.get('structures', {
    params: { projectId }
  });
  const projectStructures =
    structuresRequest.data as CustomerStructureForDatabaseType[];

  store.dispatch(
    setStructureOptions(
      projectStructures.map(({ name, uniqueId }) => ({
        label: name ?? `Unnamed Structure (${uniqueId.slice(-5)})`,
        uniqueId
      }))
    )
  );

  const success = await loadProjectWithStructure(
    projectId,
    structureIdParam,
    instanceIdParam,
    projectStructures
  );

  return { success };
}
